import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

type SelectionMode = "grid" | "list";

interface SelectionContextValue {
  selectedIds: Set<string>;
  orderedIds: string[];
  setOrderedIds: (ids: string[]) => void;
  cardClick: (id: string, e: React.MouseEvent | MouseEvent) => void;
  rowClick: (id: string, e: React.MouseEvent | MouseEvent) => void;
  selectOnly: (id: string) => void;
  clear: () => void;
  clearIfOutside: (e: React.MouseEvent) => void;
  isSelected: (id: string) => boolean;
  selectionCount: number;
  /**
   * Start a marquee (drag-rectangle) interaction. Snapshots the current
   * selection so it can be restored on cancel or unioned with the live
   * hit set when `additive` is true (shift/cmd/ctrl/alt held at
   * mousedown).
   */
  beginMarquee: (additive: boolean) => void;
  /**
   * Apply a live hit set from the marquee. When additive, unions with
   * the snapshotted prior selection so items outside the rect that were
   * already selected stay selected. When non-additive, replaces the
   * selection with `hitIds`.
   */
  updateMarquee: (hitIds: Set<string>) => void;
  /**
   * Finish a marquee interaction. `commit: false` restores the
   * pre-marquee selection (used for Escape/cancel); `commit: true`
   * leaves the current selection as-is.
   */
  endMarquee: (commit: boolean) => void;
}

function sameContents(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

const SelectionContext = createContext<SelectionContextValue | null>(null);

export function WorkspaceSelectionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [orderedIds, setOrderedIdsState] = useState<string[]>([]);
  const anchorRef = useRef<string | null>(null);
  /** Snapshot of selectedIds captured at marquee start; null while no
   *  marquee is in flight. Used for cancel-restore and additive mode. */
  const marqueePriorRef = useRef<Set<string> | null>(null);
  /** Whether the in-flight marquee was started with a modifier held. */
  const marqueeAdditiveRef = useRef<boolean>(false);
  const location = useLocation();

  const setOrderedIds = useCallback((ids: string[]) => {
    setOrderedIdsState((prev) => (sameContents(prev, ids) ? prev : ids));
  }, []);

  useEffect(() => {
    setSelectedIds(new Set());
    anchorRef.current = null;
  }, [location.pathname]);

  const handleClick = useCallback(
    (id: string, e: React.MouseEvent | MouseEvent, mode: SelectionMode) => {
      const isShift = e.shiftKey;
      const isToggle = e.metaKey || e.ctrlKey || e.altKey;

      setSelectedIds((prev) => {
        /** List shift-click: replace selection with the contiguous range from
         *  the anchor to the clicked row. Mirrors Finder list-view behavior. */
        if (mode === "list" && isShift && anchorRef.current) {
          const anchorIdx = orderedIds.indexOf(anchorRef.current);
          const targetIdx = orderedIds.indexOf(id);
          if (anchorIdx !== -1 && targetIdx !== -1) {
            const start = Math.min(anchorIdx, targetIdx);
            const end = Math.max(anchorIdx, targetIdx);
            return new Set(orderedIds.slice(start, end + 1));
          }
        }

        /** Additive toggle. In grid mode, shift also counts as a toggle
         *  modifier per the spec; in list mode only option/cmd/ctrl do. */
        if (isToggle || (mode === "grid" && isShift)) {
          const next = new Set(prev);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          anchorRef.current = id;
          return next;
        }

        anchorRef.current = id;
        return new Set([id]);
      });
    },
    [orderedIds],
  );

  const cardClick = useCallback(
    (id: string, e: React.MouseEvent | MouseEvent) =>
      handleClick(id, e, "grid"),
    [handleClick],
  );

  const rowClick = useCallback(
    (id: string, e: React.MouseEvent | MouseEvent) =>
      handleClick(id, e, "list"),
    [handleClick],
  );

  const selectOnly = useCallback((id: string) => {
    anchorRef.current = id;
    setSelectedIds(new Set([id]));
  }, []);

  const clear = useCallback(() => {
    setSelectedIds(new Set());
    anchorRef.current = null;
  }, []);

  const clearIfOutside = useCallback(
    (e: React.MouseEvent) => {
      if (selectedIds.size === 0) return;
      const target = e.target as HTMLElement | null;
      if (!target?.closest?.("[data-workspace-item]")) clear();
    },
    [selectedIds, clear],
  );

  const isSelected = useCallback(
    (id: string) => selectedIds.has(id),
    [selectedIds],
  );

  const beginMarquee = useCallback((additive: boolean) => {
    marqueeAdditiveRef.current = additive;
    /** Functional setState so we capture the latest selection even if
     *  the caller hasn't re-rendered with it yet (e.g. rapid
     *  click-to-drag). */
    setSelectedIds((prev) => {
      marqueePriorRef.current = new Set(prev);
      return additive ? prev : new Set();
    });
  }, []);

  const updateMarquee = useCallback((hitIds: Set<string>) => {
    const prior = marqueePriorRef.current;
    if (!prior) return;
    if (marqueeAdditiveRef.current) {
      /** Union, but only grow — items in `prior` outside the rect stay.
       *  If nothing new is hit, the selection is exactly `prior` again. */
      if (hitIds.size === 0) {
        setSelectedIds(new Set(prior));
        return;
      }
      const next = new Set(prior);
      for (const id of hitIds) next.add(id);
      setSelectedIds(next);
      return;
    }
    setSelectedIds(new Set(hitIds));
  }, []);

  const endMarquee = useCallback((commit: boolean) => {
    const prior = marqueePriorRef.current;
    marqueePriorRef.current = null;
    marqueeAdditiveRef.current = false;
    if (!commit && prior) {
      setSelectedIds(prior);
      return;
    }
    /** On commit we intentionally leave anchorRef alone — subsequent
     *  shift-clicks extend from the most-recent card click, which is a
     *  saner anchor than "wherever the marquee ended". */
  }, []);

  const value = useMemo<SelectionContextValue>(
    () => ({
      selectedIds,
      orderedIds,
      setOrderedIds,
      cardClick,
      rowClick,
      selectOnly,
      clear,
      clearIfOutside,
      isSelected,
      selectionCount: selectedIds.size,
      beginMarquee,
      updateMarquee,
      endMarquee,
    }),
    [
      selectedIds,
      orderedIds,
      setOrderedIds,
      cardClick,
      rowClick,
      selectOnly,
      clear,
      clearIfOutside,
      isSelected,
      beginMarquee,
      updateMarquee,
      endMarquee,
    ],
  );

  return (
    <SelectionContext.Provider value={value}>
      {children}
    </SelectionContext.Provider>
  );
}

export function useWorkspaceSelection() {
  const ctx = useContext(SelectionContext);
  if (!ctx) {
    throw new Error(
      "useWorkspaceSelection must be used within WorkspaceSelectionProvider",
    );
  }
  return ctx;
}

export function useWorkspaceSelectionOptional() {
  return useContext(SelectionContext);
}
